import React, { useState, useCallback, useEffect, memo } from 'react';
import { Formik, Form, Field } from 'formik';
import {
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
    Alert,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Tooltip,
    Paper
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useTravelPlans } from 'hooks/api/travel-plan/useTravelPlans';
import { useProvince } from 'hooks/api/terminology/province/useProvince';
import { useDistrict } from 'hooks/api/terminology/district/useDistrict';
import Swal from 'sweetalert2';
import useSite from 'hooks/api/terminology/site/useSite';
import { globalColor } from '../ConstantColors';

const TravelPlanSchema = Yup.object().shape({
    planName: Yup.string()
        .required('Plan name is required')
        .min(3, 'Plan name must be at least 3 characters')
        .max(100, 'Plan name cannot exceed 100 characters'),
    departureTime: Yup.date()
        .required('Departure time is required')
        .min(new Date(), 'Departure time cannot be in the past')
        .test('future-time', 'Departure time must be at least 1 hour from now', (value) => {
            if (!value) return false;
            const minTime = new Date();
            minTime.setHours(minTime.getHours() + 1);
            return value >= minTime;
        }),
    returnTime: Yup.date()
        .required('Return time is required')
        .min(Yup.ref('departureTime'), 'Return time must be after departure time')
        .test('min-duration', 'Trip must be at least 1 hour long', (value, { parent }) => {
            const { departureTime } = parent;
            if (!value || !departureTime) return true;
            const hourDiff = (value - new Date(departureTime)) / (1000 * 60 * 60);
            return hourDiff >= 1;
        }),
    type: Yup.string().required('Travel type is required').oneOf(['LUNCH_INCLUDED', 'LUNCH_EXCLUDED'], 'Invalid travel type'),
    venueId: Yup.string().required('Venue selection is required'),
    provinceId: Yup.string().required('Province selection is required'),
    districtId: Yup.string().required('District selection is required')
});

const FileUploadSection = ({ files, onRemoveFile, onRemoveAllFiles, getRootProps, getInputProps, isDragActive, fileError }) => {
    let borderColor = 'grey.400';
    if (isDragActive) borderColor = 'primary.main';
    else if (fileError) borderColor = 'error.main';

    return (
        <Paper elevation={0} variant="outlined" sx={{ p: { xs: 1, sm: 2 }, mt: { xs: 1, sm: 2 }, minWidth: 0 }}>
            <Box
                {...getRootProps()}
                sx={{
                    border: '2px dashed',
                    borderColor,
                    borderRadius: 1,
                    p: { xs: 1.5, sm: 3 },
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                    '&:hover': {
                        bgcolor: 'action.hover'
                    }
                }}
            >
                <input {...getInputProps()} />
                <AttachFileIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: fileError ? 'error.main' : 'primary.main', mb: 0.5 }} />
                <Typography color={fileError ? 'error' : 'textSecondary'} sx={{ overflowWrap: 'anywhere' }}>
                    {isDragActive ? 'Drop files here...' : 'Drag & drop files here, or click to select files (optional)'}
                </Typography>
                <Typography variant="caption" color={fileError ? 'error' : 'textSecondary'}>
                    Accepted files: PDF, JPG, PNG
                </Typography>
            </Box>

            {files.length > 0 && (
                <Box mt={{ xs: 1, sm: 2 }} sx={{ minWidth: 0 }}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        gap={1}
                        mb={1}
                    >
                        <Typography variant="subtitle2">Attached Files ({files.length})</Typography>
                        <Tooltip title="Remove all files">
                            <Button
                                startIcon={<ClearAllIcon />}
                                size="small"
                                color="error"
                                onClick={onRemoveAllFiles}
                                variant="outlined"
                                sx={{ alignSelf: { xs: 'stretch', sm: 'auto' }, minHeight: 44 }}
                            >
                                Clear All
                            </Button>
                        </Tooltip>
                    </Box>
                    <List dense>
                        {files.map((file, index) => (
                            <ListItem
                                key={index}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    mb: 0.5,
                                    minWidth: 0,
                                    pr: 6
                                }}
                            >
                                <ListItemText
                                    primary={file.name}
                                    secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                                    sx={{ minWidth: 0 }}
                                    primaryTypographyProps={{ noWrap: true, title: file.name }}
                                />
                                <ListItemSecondaryAction>
                                    <Tooltip title="Remove file">
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            onClick={() => onRemoveFile(index)}
                                            color="error"
                                            size="small"
                                            sx={{ minWidth: 44, minHeight: 44 }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
        </Paper>
    );
};

const TravelPlanForm = memo(({ open, handleClose, onOpenTravelRequest }) => {
    const { createPlan, loading } = useTravelPlans();
    const { listProvince, provinces } = useProvince();
    const { districts, fetchDistrictsByProvince } = useDistrict();
    const { sites, fetchSiteByDistrict } = useSite();
    const [files, setFiles] = useState([]);
    const [formError, setFormError] = useState('');
    const [fileError, setFileError] = useState('');

    // Get current date and time with minimum 1 hour ahead
    const getMinDateTime = () => {
        const now = new Date();
        now.setHours(now.getHours() + 1);
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        return now.toISOString().slice(0, 16);
    };

    useEffect(() => {
        listProvince(0, 100);
    }, [listProvince]);

    const handleProvinceChange = (provinceId) => {
        fetchDistrictsByProvince(provinceId);
    };

    const handleDistrictChange = (districtId) => {
        fetchSiteByDistrict(districtId);
    };

    const handleRemoveFile = (indexToRemove) => {
        setFiles((prevFiles) => prevFiles.filter((_, index) => index !== indexToRemove));
        setFileError('');
    };

    const handleRemoveAllFiles = () => {
        Swal.fire({
            title: 'Remove all files?',
            text: 'Are you sure you want to remove all attached files?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: globalColor,
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, remove all',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                setFiles([]);
                setFileError('');
            }
        });
    };

    const onDrop = useCallback(
        (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles.length > 0) {
                const errors = rejectedFiles.map((file) => {
                    const errorMessages = [];
                    if (file.file.size > 5242880) errorMessages.push(`${file.file.name} is too large (max 5MB)`);
                    if (!file.file.type.match(/(pdf|jpeg|jpg|png)/)) {
                        errorMessages.push(`${file.file.name} has invalid type`);
                    }
                    return errorMessages.join(', ');
                });
                setFileError(`Upload failed: ${errors.join('; ')}`);
                return;
            }

            // Filter out Word documents
            const filteredFiles = acceptedFiles.filter((file) => {
                const fileName = file.name.toLowerCase();
                const isWordDocument = fileName.endsWith('.doc') || fileName.endsWith('.docx') || fileName.endsWith('.odt');
                if (isWordDocument) {
                    setFileError(`${file.name} is not allowed. Word documents (.doc, .docx, .odt) are not supported.`);
                    return false;
                }
                return true;
            });

            if (filteredFiles.length === 0) {
                return;
            }

            const newFiles = filteredFiles.filter((file) => {
                const isDuplicate = files.some((existingFile) => existingFile.name === file.name && existingFile.size === file.size);
                return !isDuplicate;
            });

            if (newFiles.length > 0) {
                setFiles((prevFiles) => [...prevFiles, ...newFiles]);
                setFileError('');
            } else {
                setFileError('Selected files are already attached');
            }
        },
        [files]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png']
        },
        maxSize: 5242880,
        multiple: true,
        onDropRejected: (rejectedFiles) => {
            const wordDocErrors = rejectedFiles.filter((file) => {
                const fileName = file.file.name.toLowerCase();
                return fileName.endsWith('.doc') || fileName.endsWith('.docx') || fileName.endsWith('.odt');
            });

            if (wordDocErrors.length > 0) {
                setFileError('Word documents (.doc, .docx, .odt) are not allowed. Please use PDF, JPG, or PNG files only.');
            }
        }
    });

    const handleFormSubmit = async (values, { resetForm, setSubmitting }) => {
        try {
            const formData = new FormData();
            const departureDate = new Date(values.departureTime);
            const returnDate = new Date(values.returnTime);

            const planData = {
                planName: values.planName.trim(),
                // departureTime: new Date(values.departureTime).toISOString(),
                // returnTime: new Date(values.returnTime).toISOString(),
                departureTime: new Date(departureDate.getTime() - departureDate.getTimezoneOffset() * 60000).toISOString(),
                returnTime: new Date(returnDate.getTime() - returnDate.getTimezoneOffset() * 60000).toISOString(),
                type: values.type,
                venue: values.venueId,
                documentTypes: files.map(() => 'JUSTIFICATION')
            };

            formData.append(
                'travelPlan',
                new Blob([JSON.stringify(planData)], {
                    type: 'application/json'
                })
            );

            // Only append files if they exist
            files.forEach((file) => formData.append('files', file));

            const response = await createPlan(formData);

            if (response) {
                const result = await Swal.fire({
                    title: 'Success!',
                    text: 'Travel plan created successfully. Would you like to create a travel request?',
                    icon: 'success',
                    showCancelButton: true,
                    confirmButtonColor: globalColor,
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes, create request',
                    cancelButtonText: 'No, thanks'
                });

                resetForm();
                setFiles([]);
                setFileError('');
                setFormError('');
                handleClose();

                if (result.isConfirmed) {
                    onOpenTravelRequest(response);
                }
            }
        } catch (error) {
            setFormError(error.message || 'An error occurred while creating the travel plan');
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: { xs: 'calc(100vh - 16px)', sm: '60vh' },
                    height: { xs: 'calc(100vh - 16px)', sm: 'auto' },
                    maxHeight: { xs: 'calc(100vh - 16px)', sm: '90vh' },
                    width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
                    maxWidth: { xs: 'none', md: 900 },
                    m: { xs: 1, sm: 4 },
                    overflow: 'hidden',
                    '@supports (height: 100dvh)': {
                        minHeight: { xs: 'calc(100dvh - 16px)', sm: '60vh' },
                        height: { xs: 'calc(100dvh - 16px)', sm: 'auto' },
                        maxHeight: { xs: 'calc(100dvh - 16px)', sm: '90vh' }
                    }
                }
            }}
        >
            <DialogTitle
                sx={{
                    flex: '0 0 auto',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 2 },
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5" sx={{ color: '#141b2d', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 700 }}>
                        New Travel Plan
                    </Typography>
                    <IconButton aria-label="Close new travel plan" onClick={handleClose} edge="end" sx={{ minWidth: 44, minHeight: 44 }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Formik
                initialValues={{
                    planName: '',
                    departureTime: '',
                    returnTime: '',
                    type: '',
                    venueId: '',
                    provinceId: '',
                    districtId: ''
                }}
                validationSchema={TravelPlanSchema}
                onSubmit={handleFormSubmit}
            >
                {({ errors, touched, handleChange, values, setFieldValue }) => (
                    <Form style={{ display: 'flex', flex: '1 1 auto', minHeight: 0, flexDirection: 'column', overflow: 'hidden' }}>
                        <DialogContent
                            sx={{
                                flex: '1 1 auto',
                                minHeight: 0,
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                px: { xs: 1.5, sm: 3 },
                                py: { xs: 2, sm: 2.5 },
                                '& .MuiInputBase-root': {
                                    minWidth: 0,
                                    maxWidth: '100%'
                                },
                                '& .MuiOutlinedInput-root': {
                                    minHeight: { xs: 48, sm: 56 }
                                },
                                '& input': {
                                    minWidth: 0,
                                    maxWidth: '100%'
                                },
                                '& input[type="datetime-local"]': {
                                    minWidth: 0,
                                    width: '100%'
                                }
                            }}
                        >
                            {(formError || fileError) && (
                                <Alert
                                    severity="error"
                                    sx={{ mb: 2 }}
                                    onClose={() => {
                                        setFormError('');
                                        setFileError('');
                                    }}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={1} sx={{ minWidth: 0 }}>
                                        <WarningIcon />
                                        <Typography sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{formError || fileError}</Typography>
                                    </Box>
                                </Alert>
                            )}

                            <Grid container spacing={{ xs: 1.5, md: 2 }}>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="planName"
                                        label="Plan Name"
                                        fullWidth
                                        error={touched.planName && Boolean(errors.planName)}
                                        helperText={touched.planName && errors.planName}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth error={touched.provinceId && Boolean(errors.provinceId)}>
                                        <InputLabel>Province</InputLabel>
                                        <Select
                                            name="provinceId"
                                            value={values.provinceId}
                                            onChange={(e) => {
                                                handleChange(e);
                                                setFieldValue('districtId', '');
                                                setFieldValue('venueId', '');
                                                handleProvinceChange(e.target.value);
                                            }}
                                            label="Province"
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxWidth: 'calc(100vw - 24px)',
                                                        '& .MuiMenuItem-root': {
                                                            whiteSpace: 'normal',
                                                            overflowWrap: 'anywhere'
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            {provinces.map((province) => (
                                                <MenuItem key={province.id} value={province.id}>
                                                    {province.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {touched.provinceId && errors.provinceId && <FormHelperText>{errors.provinceId}</FormHelperText>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl
                                        fullWidth
                                        error={touched.districtId && Boolean(errors.districtId)}
                                        disabled={!values.provinceId}
                                    >
                                        <InputLabel>District</InputLabel>
                                        <Select
                                            name="districtId"
                                            value={values.districtId}
                                            onChange={(e) => {
                                                handleChange(e);
                                                setFieldValue('venueId', '');
                                                handleDistrictChange(e.target.value);
                                            }}
                                            label="District"
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxWidth: 'calc(100vw - 24px)',
                                                        '& .MuiMenuItem-root': {
                                                            whiteSpace: 'normal',
                                                            overflowWrap: 'anywhere'
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            {districts.map((district) => (
                                                <MenuItem key={district.id} value={district.id}>
                                                    {district.name}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        {touched.districtId && errors.districtId && <FormHelperText>{errors.districtId}</FormHelperText>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth error={touched.type && Boolean(errors.type)}>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            name="type"
                                            value={values.type}
                                            onChange={handleChange}
                                            label="Type"
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxWidth: 'calc(100vw - 24px)',
                                                        '& .MuiMenuItem-root': {
                                                            whiteSpace: 'normal',
                                                            overflowWrap: 'anywhere'
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem value="LUNCH_INCLUDED">Lunch is Covered by Host/Organizer</MenuItem>
                                            <MenuItem value="LUNCH_EXCLUDED">Need to Claim Lunch Allowance</MenuItem>
                                        </Select>
                                        {touched.type && errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth error={touched.venueId && Boolean(errors.venueId)} disabled={!values.districtId}>
                                        <InputLabel>Venue</InputLabel>
                                        <Select
                                            name="venueId"
                                            value={values.venueId}
                                            onChange={handleChange}
                                            label="Venue"
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxWidth: 'calc(100vw - 24px)',
                                                        '& .MuiMenuItem-root': {
                                                            whiteSpace: 'normal',
                                                            overflowWrap: 'anywhere'
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            {sites.map((site) => (
                                                <MenuItem key={site.id} value={site.id}>
                                                    {site.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {touched.venueId && errors.venueId && <FormHelperText>{errors.venueId}</FormHelperText>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Field
                                        as={TextField}
                                        name="departureTime"
                                        label="Departure Time"
                                        type="datetime-local"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            min: getMinDateTime()
                                        }}
                                        error={touched.departureTime && Boolean(errors.departureTime)}
                                        helperText={touched.departureTime && errors.departureTime}
                                        onChange={(e) => {
                                            handleChange(e);
                                            if (values.returnTime && new Date(values.returnTime) <= new Date(e.target.value)) {
                                                setFieldValue('returnTime', '');
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Field
                                        as={TextField}
                                        name="returnTime"
                                        label="Return Time"
                                        type="datetime-local"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            min: values.departureTime || getMinDateTime()
                                        }}
                                        error={touched.returnTime && Boolean(errors.returnTime)}
                                        helperText={
                                            (touched.returnTime && errors.returnTime) ||
                                            (values.departureTime && !values.returnTime && 'Select a return time after departure')
                                        }
                                        disabled={!values.departureTime}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FileUploadSection
                                        files={files}
                                        onRemoveFile={handleRemoveFile}
                                        onRemoveAllFiles={handleRemoveAllFiles}
                                        getRootProps={getRootProps}
                                        getInputProps={getInputProps}
                                        isDragActive={isDragActive}
                                        fileError={fileError}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>

                        <DialogActions
                            sx={{
                                flex: '0 0 auto',
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                p: { xs: 1.5, sm: 2.5 },
                                pb: { xs: 'calc(12px + env(safe-area-inset-bottom))', sm: 2.5 },
                                gap: 1,
                                '& > :not(style) ~ :not(style)': {
                                    ml: 0
                                }
                            }}
                        >
                            <Button
                                onClick={handleClose}
                                variant="outlined"
                                color="error"
                                sx={{
                                    flex: { xs: 1, sm: '0 0 auto' },
                                    minHeight: 44,
                                    px: { xs: 1.5, sm: 3 }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={loading || Object.keys(errors).length > 0}
                                sx={{ flex: { xs: 1, sm: '0 0 auto' }, minHeight: 44, px: { xs: 1.5, sm: 4 } }}
                            >
                                {loading ? 'Submitting...' : 'Submit'}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
});

TravelPlanForm.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    onOpenTravelRequest: PropTypes.func.isRequired
};

FileUploadSection.propTypes = {
    files: PropTypes.array.isRequired,
    onRemoveFile: PropTypes.func.isRequired,
    onRemoveAllFiles: PropTypes.func.isRequired,
    getRootProps: PropTypes.func.isRequired,
    getInputProps: PropTypes.func.isRequired,
    isDragActive: PropTypes.bool.isRequired,
    fileError: PropTypes.string
};

export default TravelPlanForm;
